/**
 * TanStack Query hooks for `campaign-clone` (FS-820).
 *
 * Exposes:
 *   - useCampaignList()  -> list of campaigns used by the source picker
 *   - useCloneCampaign() -> mutation that POSTs /api/v1/campaign-clone/:id
 *
 * Both hooks are thin wrappers around ./api and lean on the `campaigns`
 * invalidation key so a fresh clone shows up the next time the operator
 * navigates to the campaign dashboard.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	campaignCloneQueryKeys,
	cloneCampaign,
	listCampaigns,
	type CloneRequest,
	type CloneResponse,
	type ListSourcesResponse,
} from "./api";

export function useCampaignList(): UseQueryResult<ListSourcesResponse, Error> {
	return useQuery<ListSourcesResponse, Error>({
		queryKey: campaignCloneQueryKeys.sources(),
		queryFn: ({ signal }) => listCampaigns(signal),
		staleTime: 30_000,
		refetchOnWindowFocus: false,
		retry: 1,
	});
}

export function useCloneCampaign(): UseMutationResult<
	CloneResponse,
	Error,
	CloneRequest
> {
	const qc = useQueryClient();
	return useMutation<CloneResponse, Error, CloneRequest>({
		mutationFn: cloneCampaign,
		onSuccess: (data) => {
			// Invalidate both this feature's source list and any sibling campaign
			// list cache so the cloned row appears on return to the dashboard.
			qc.invalidateQueries({ queryKey: campaignCloneQueryKeys.all });
			qc.invalidateQueries({ queryKey: ["campaigns"] });
			toast.success(`Campaign cloned — ${data.campaign.name}`);
		},
		onError: (err) => {
			toast.error(err.message || "Clone failed");
		},
	});
}
